import { Controller } from "@hotwired/stimulus"

// Log viewer: click-to-expand, keyboard shortcuts, live tail via Turbo Streams
export default class extends Controller {
  static targets = ["line", "detail", "liveButton", "liveDot", "streamContainer"]
  static values = { projectId: String, live: { type: Boolean, default: false } }

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.stopLive()
  }

  // ── Click to expand/collapse a log line ──────────────────────
  toggleExpand(event) {
    const line = event.currentTarget
    const detail = line.querySelector("[data-log-viewer-target='detail']")
    if (!detail) return

    // Close other expanded lines
    this.element.querySelectorAll("[data-log-viewer-target='detail']").forEach(d => {
      if (d !== detail) d.classList.add("hidden")
    })

    detail.classList.toggle("hidden")
  }

  // Prevent clicks on links inside expanded detail from toggling
  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Keyboard shortcuts ───────────────────────────────────────
  handleKeydown(event) {
    // "/" focuses search (if not already in an input)
    if (event.key === "/" && !this.isInputFocused()) {
      event.preventDefault()
      const searchInput = document.querySelector("[data-search-target='input']")
      if (searchInput) searchInput.focus()
    }

    // Escape closes all expanded details
    if (event.key === "Escape") {
      this.element.querySelectorAll("[data-log-viewer-target='detail']").forEach(d => {
        d.classList.add("hidden")
      })
      // Also blur search
      if (document.activeElement?.matches("input")) {
        document.activeElement.blur()
      }
    }
  }

  isInputFocused() {
    const el = document.activeElement
    return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)
  }

  // ── Live tail ────────────────────────────────────────────────
  toggleLive() {
    this.liveValue = !this.liveValue
    if (this.liveValue) {
      this.startLive()
    } else {
      this.stopLive()
    }
  }

  startLive() {
    if (!this.hasStreamContainerTarget) return

    // Inject a <turbo-cable-stream-source> for Turbo Streams
    const projectId = this.projectIdValue
    this.streamContainerTarget.innerHTML =
      `<turbo-cable-stream-source channel="Turbo::StreamsChannel" signed-stream-name="${this.getSignedStreamName()}" connected></turbo-cable-stream-source>`

    // We can't easily get the signed name client-side, so use a simpler approach:
    // Fetch the signed stream tag from the server
    fetch(`/logs/live_stream?project_id=${projectId}`, {
      headers: { "Accept": "text/html" }
    })
    .then(r => r.text())
    .then(html => {
      this.streamContainerTarget.innerHTML = html
    })
    .catch(() => {
      // Fallback: just poll
      this.liveValue = false
      this.updateLiveUI()
    })

    this.updateLiveUI()
  }

  stopLive() {
    if (this.hasStreamContainerTarget) {
      this.streamContainerTarget.innerHTML = ""
    }
    this.liveValue = false
    this.updateLiveUI()
  }

  updateLiveUI() {
    if (!this.hasLiveButtonTarget) return

    if (this.liveValue) {
      this.liveDotTarget.innerHTML = `
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
        <span class="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
      `
      this.liveButtonTarget.classList.add("btn-success", "btn-outline")
      this.liveButtonTarget.classList.remove("btn-ghost")
    } else {
      this.liveDotTarget.innerHTML = `
        <span class="relative inline-flex rounded-full h-2 w-2 bg-base-content/30"></span>
      `
      this.liveButtonTarget.classList.remove("btn-success", "btn-outline")
      this.liveButtonTarget.classList.add("btn-ghost")
    }
  }

  // Not used — we fetch the signed name from the server
  getSignedStreamName() {
    return ""
  }
}
